import React, { useState, useEffect, useRef } from 'react';
import '../custom.css';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Toast } from 'primereact/toast';
import { InputText } from 'primereact/inputtext';
import { FilterMatchMode } from 'primereact/api';
import { Tag } from 'primereact/tag';
import { ColumnGroup } from 'primereact/columngroup';
import { Row } from 'primereact/row';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { getInstituteList, filterInstituteList, cancelInstituteOrder } from '../utilities/api';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx'; // Ensure you run: npm install xlsx

const OrderList = () => {
    const toast = useRef(null);
    const dt = useRef(null);
    const navigate = useNavigate();

    const [rawBranches, setRawBranches] = useState([]);
    const [tableData, setTableData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dates, setDates] = useState(null);
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState('pending');
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const [filters, setFilters] = useState({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS }
    });

    const statusOptions = [
        { label: 'Pending', value: 'pending' },
        { label: 'Dispatched', value: 'dispatched' },
        { label: 'Settled', value: 'settled' },
        { label: 'Cancelled', value: 'cancelled' }
    ];

    useEffect(() => {
        loadInitialData();
    }, []);

    const loadInitialData = async () => {
        try {
            const data = await getInstituteList({ rolename: localStorage.getItem('role'), clientname: localStorage.getItem('clientid') });
            setRawBranches(data.branchs || []);
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        if (dates && dates[0] && !dates[1]) return;
        fetchFilteredData();
    }, [dates, selectedBranch, selectedStatus]);

    const fetchFilteredData = async () => {
        setLoading(true);
        const startDate = dates && dates[0] ? moment(dates[0]).format('YYYY-MM-DD') : '';
        const endDate = dates && dates[1] ? moment(dates[1]).format('YYYY-MM-DD') : '';
        try {
            const data = await filterInstituteList({
                rolename: localStorage.getItem('role'),
                clientname: localStorage.getItem('clientid'),
                from_date: startDate,
                to_date: endDate,
                branch_id: selectedBranch || '',
                status: selectedStatus || ''
            });
            setTableData(data.bills || []);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    // --- EXPORT LOGIC ---
    const exportExcel = () => {
        const branchName = getBranchName();
        const rangeLabel = getDateRangeLabel();
        const genBy = localStorage.getItem('role') || 'Admin';
        const totalValue = tableData.reduce((acc, item) => acc + parseFloat(item.total_paid_amount || 0), 0);

        // 1. Prepare Header Info
        const headerInfo = [
            ['ORDER LIST REPORT'],
            [`Branch: ${branchName}`],
            [`Date Filter: ${rangeLabel}`],
            [`Generated By: ${genBy}`, '', '', '', `Generated On: ${moment().format('DD-MM-YYYY HH:mm')}`],
            [] // Spacer
        ];

        // 2. Prepare Table Data
        const isDispatched = selectedStatus === 'dispatched';
        const worksheetData = tableData.map((item, index) => ({
            'S.No': index + 1,
            'Bill No': item.bill_no,
            [isDispatched ? 'Delivery Date' : 'Bill Date']: moment(isDispatched ? item.delivery_date : item.bill_date).format('DD-MM-YYYY'),
            'Invoice No': item.invoice_no || '-',
            Customer: item.company_name,
            Amount: parseFloat(item.total_paid_amount || 0),
            Status: item.status.toUpperCase()
        }));

        // 3. Create Worksheet
        const worksheet = XLSX.utils.json_to_sheet(worksheetData, { origin: 'A6' });

        // 4. Add Headers at the top
        XLSX.utils.sheet_add_aoa(worksheet, headerInfo, { origin: 'A1' });

        // 5. Add Grand Total at the bottom
        const lastRowIndex = worksheetData.length + 7;
        XLSX.utils.sheet_add_aoa(worksheet, [['', '', '', '', 'GRAND TOTAL:', totalValue]], { origin: `A${lastRowIndex}` });

        // --- 6. AUTOFIT COLUMN WIDTHS ---
        const objectMaxLength = [];
        // Combine headers and data to find the widest string in each column
        const allRows = [...headerInfo, Object.keys(worksheetData[0] || {}), ...worksheetData.map(Object.values)];

        allRows.forEach((row) => {
            row.forEach((val, i) => {
                const columnValue = val ? val.toString() : '';
                objectMaxLength[i] = Math.max(objectMaxLength[i] || 10, columnValue.length + 2);
            });
        });

        worksheet['!cols'] = objectMaxLength.map((width) => ({ width }));

        // 7. Generate and Save
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Orders');
        XLSX.writeFile(workbook, `OrderReport_${branchName.replace(/\s+/g, '_')}.xlsx`);
    };

    const exportPdf = () => {
        const doc = new jsPDF('p', 'pt', 'a4');
        const total = tableData.reduce((acc, item) => acc + parseFloat(item.total_paid_amount || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2 });

        // --- Header Section ---
        doc.setFontSize(14).setTextColor(0).setFont('helvetica', 'bold').text('ORDER LIST REPORT', 40, 40);

        doc.setFontSize(9).setFont('helvetica', 'normal').setTextColor(50);
        doc.text(`Branch: ${getBranchName()}`, 40, 60);
        doc.text(`Date Filter: ${getDateRangeLabel()}`, 40, 75);

        doc.text(`Generated By: ${generatedBy}`, 400, 60);
        doc.text(`Generated On: ${currentDateTime}`, 400, 75);

        doc.line(40, 85, 550, 85); // Divider line

        const isDispatchedPdf = selectedStatus === 'dispatched';
        const exportColumns = ['S.No', 'Bill No', isDispatchedPdf ? 'Delivery Date' : 'Date', 'Inv No', 'Customer', 'Amount', 'Status'];
        const exportRows = tableData.map((item, i) => [i + 1, item.bill_no, moment(isDispatchedPdf ? item.delivery_date : item.bill_date).format('DD-MM-YYYY'), item.invoice_no || '-', item.company_name, item.total_paid_amount, item.status.toUpperCase()]);

        autoTable(doc, {
            head: [exportColumns],
            body: exportRows,
            startY: 100, // Starts after the header text
            theme: 'grid',
            styles: { fontSize: 8 },
            headStyles: { fillColor: [33, 150, 243] },
            foot: [[{ content: 'GRAND TOTAL', colSpan: 5, styles: { halign: 'right', fontStyle: 'bold', textColor: 0 } }, { content: total, styles: { halign: 'right', fontStyle: 'bold', textColor: 0 } }, { content: '' }]],
            showFoot: 'lastPage'
        });

        doc.save(`OrderReport_${getBranchName()}_${moment().format('DDMMYYYY')}.pdf`);
    };
    // --- TEMPLATES ---
    const formatDate = (dateString) => (dateString ? moment(dateString).format('DD-MM-YYYY') : '');
    const actionBodyTemplate = (rowData) => (
        <div className="flex gap-1 justify-content-center">
            <Button icon="pi pi-file-pdf" className="p-button-rounded p-button-info p-button-text p-0" onClick={() => navigate(`/orderview/${rowData.bill_id}`)} tooltip="View" />
            <Button icon="pi pi-pencil" className="p-button-rounded p-button-warning p-button-text p-0" onClick={() => navigate(`/orderedit/${rowData.bill_id}`)} disabled={rowData.status !== 'pending'} tooltip="Edit" />
            <Button
                icon="pi pi-times"
                className="p-button-rounded p-button-danger p-button-text p-0"
                onClick={() =>
                    confirmDialog({
                        message: `Are you sure you want to cancel Bill: ${rowData.bill_no}?`,
                        header: 'Confirm Cancellation',
                        icon: 'pi pi-exclamation-triangle',
                        acceptClassName: 'p-button-danger',
                        accept: () => cancelBill(rowData.bill_id) // <--- Call the function here
                    })
                }
                disabled={rowData.status === 'cancelled' || rowData.status === 'settled'}
                tooltip="Cancel"
            />
        </div>
    );
    const cancelBill = async (billId) => {
        try {
            setLoading(true);
            const data = await cancelInstituteOrder({
                bill_id: billId,
                cancelled_by: localStorage.getItem('role'),
                clientname: localStorage.getItem('clientid')
            });
            if (data.status === 'success' || data.code === 200 || data.status === true) {
                toast.current.show({
                    severity: 'success',
                    summary: 'Cancelled',
                    detail: `Bill has been cancelled successfully`,
                    life: 3000
                });
                fetchFilteredData();
            } else {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: data.message || 'Failed to cancel bill',
                    life: 3000
                });
            }
        } catch (error) {
            console.error('Cancel Error:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Server connection failed',
                life: 3000
            });
        } finally {
            setLoading(false);
        }
    };
    const renderHeader = () => (
        <div className="flex justify-content-between align-items-center">
            <div className="flex gap-2">
                <Button type="button" icon="pi pi-file-excel" severity="success" rounded onClick={exportExcel} tooltip="Excel Export" />
                <Button type="button" icon="pi pi-file-pdf" severity="warning" rounded onClick={exportPdf} tooltip="PDF Export" />
            </div>
            <span className="p-input-icon-left">
                <i className="pi pi-search" />
                <InputText
                    value={globalFilterValue}
                    onChange={(e) => {
                        setGlobalFilterValue(e.target.value);
                        setFilters({ ...filters, global: { value: e.target.value, matchMode: FilterMatchMode.CONTAINS } });
                    }}
                    placeholder="Search..."
                    className="p-inputtext-sm"
                />
            </span>
        </div>
    );
    const generatedBy = localStorage.getItem('role') || 'Admin';
    const currentDateTime = moment().format('DD-MM-YYYY HH:mm:ss');

    const getBranchName = () => {
        const branch = rawBranches.find((b) => b.branch_id === selectedBranch);
        return branch ? branch.branch_name : 'All Branches';
    };

    const getDateRangeLabel = () => {
        if (dates && dates[0] && dates[1]) {
            return `${moment(dates[0]).format('DD-MM-YYYY')} to ${moment(dates[1]).format('DD-MM-YYYY')}`;
        }
        return 'All Dates';
    };
    // --- Footer Logic for Totals ---
    const footerGroup = (
        <ColumnGroup>
            <Row>
                <Column footer="Grand Total:" colSpan={4} footerStyle={{ textAlign: 'right', fontWeight: 'bold' }} />
                <Column
                    footer={tableData.reduce((acc, item) => acc + parseFloat(item.total_paid_amount || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    footerStyle={{ textAlign: 'right', fontWeight: 'bold' }}
                />
                <Column colSpan={2} />
            </Row>
        </ColumnGroup>
    );
    return (
        <div className="p-2">
            <Toast ref={toast} />
            <ConfirmDialog />

            <div className="list-filter-bar">
                <div className="filter-field" style={{ flex: '1 1 140px', minWidth: '130px' }}>
                    <label>Status</label>
                    <Dropdown value={selectedStatus} options={statusOptions} onChange={(e) => setSelectedStatus(e.value)} placeholder="All Status" showClear className="w-full" />
                </div>
                <div className="filter-field" style={{ flex: '1 1 200px', minWidth: '180px' }}>
                    <label>Date Range</label>
                    <Calendar value={dates} onChange={(e) => setDates(e.value)} selectionMode="range" readOnlyInput showIcon placeholder="Select Dates" className="w-full" />
                </div>
                <div className="filter-field" style={{ flex: '1 1 175px', minWidth: '155px' }}>
                    <label>Branch</label>
                    <Dropdown value={selectedBranch} options={rawBranches} optionLabel="branch_name" optionValue="branch_id" onChange={(e) => setSelectedBranch(e.value)} placeholder="All Branches" filter showClear className="w-full" />
                </div>
                <div style={{ flex: '0 0 auto', alignSelf: 'flex-end' }}>
                    <Button
                        icon="pi pi-filter-slash"
                        className="p-button-outlined p-button-secondary"
                        tooltip="Clear Filters"
                        onClick={() => {
                            setDates(null);
                            setSelectedStatus('pending');
                            setSelectedBranch(null);
                        }}
                    />
                </div>
            </div>

            <DataTable
                ref={dt}
                value={tableData}
                loading={loading}
                paginator
                rows={10}
                header={renderHeader()}
                filters={filters}
                footerColumnGroup={footerGroup} // <--- Add this line
                className="p-datatable-gridlines p-datatable-sm shadow-1"
                size="small"
            >
                <Column field="bill_no" header="Bill No" style={{ minWidth: '7rem' }} />
                <Column
                    header={selectedStatus === 'dispatched' ? 'Delivery Date' : 'Bill Date'}
                    body={(r) =>
                        selectedStatus === 'dispatched' ? (
                            <div>
                                {formatDate(r.delivery_date)}
                                <br />
                                <small>{r.delivery_time}</small>
                            </div>
                        ) : (
                            <div>
                                {formatDate(r.bill_date)}
                                <br />
                                <small>{r.bill_time}</small>
                            </div>
                        )
                    }
                    style={{ minWidth: '8rem' }}
                />
                <Column field="invoice_no" header="Inv No" style={{ minWidth: '7rem' }} />
                <Column field="company_name" header="Customer" />
                <Column field="total_paid_amount" header="Total Paid" style={{ minWidth: '7rem', textAlign: 'right' }} body={(r) => parseFloat(r.total_paid_amount).toFixed(2)} />
                <Column
                    field="status"
                    header="Status"
                    body={(r) => <Tag value={r.status.toUpperCase()} severity={r.status === 'pending' ? 'warning' : r.status === 'cancelled' ? 'danger' : 'success'} />}
                    style={{ minWidth: '7rem', textAlign: 'center' }}
                />
                <Column header="Actions" body={actionBodyTemplate} style={{ minWidth: '8rem', textAlign: 'center' }} />
            </DataTable>
        </div>
    );
};

export default OrderList;
